<template>
  <ContentSection spacing="none" class="pb-16 transition-[height] sm:pb-28" :class="{ 'pt-12': index === 0 }">
    <Container indent="lg-but-on-mobile-default">
      <!-- HEADER -->
      <h2
        v-if="data.heading"
        v-animate-in-from-bottom
        class="mb-6 typo-heading-lg sm:mb-9 sm:typo-heading-xl"
        v-html="data.heading"
      />

      <!-- FILTER -->
      <OverviewFilter
        v-model="filter"
        class="mb-8 sm:mb-16"
        :categories="props.data.blog_categories"
        @category="filter.category = $event"
      />
    </Container>

    <Container>
      <!-- ARTICLES -->
      <TransitionGroup
        tag="ul"
        name="list"
        class="
          grid grid-cols-1 gap-5
          lg:grid-cols-[repeat(auto-fill,_minmax(24rem,_1fr))]
          sm:grid-cols-[repeat(auto-fill,_minmax(19rem,_1fr))] sm:gap-6
        "
      >
        <li v-for="article in entries" :key="article?.id">
          <CardBlogArticle :data="article" />
        </li>
      </TransitionGroup>

      <!-- PAGINATION -->
      <Pagination
        v-if="totalPages > 1"
        :page="page"
        :total-pages="totalPages"
        @paginate="fetchEntries($event)"
      />

      <!-- NO RESULTS -->
      <Transition name="fade-in">
        <NoEntriesFound v-if="!entries?.length" />
      </Transition>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { BlogArticleFragment, ComponentsMainOverviewBlogFragment } from '#gql'

const props = defineProps<{
  data: ComponentsMainOverviewBlogFragment
  index?: number
}>()

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

// Filter
export type FilterType = {
  category: string | null

}
const filter = ref<FilterType>({
  category: route.query.category ? (route.query.category as string) : null,

})

// Pagination
const limit = 16
const page = ref(route.query.page ? parseInt(route.query.page as string) : 1)
const hasMorePages = ref<boolean>(props.data.has_more_pages ?? false)
const totalPages = ref<number>(parseInt(props.data.total || '1'))

// Entries
const entries = ref<ComponentsMainOverviewBlogFragment['entries']>(props.data.entries)

// If there is a filter set as url query param, fetch entries with that filter
if (route.query.category || page.value > 1) {
  const payload = await useAsyncGql({
    operation: 'BlogArticles',
    variables: {
      sort: 'date desc',
      limit: limit,
      page: page.value,
      filter: {
        site: locale.value,
        category: { contains: route.query.category },
      },
    },
  })

  if (payload.data.value?.entries?.data) {
    entries.value = payload.data.value.entries.data
    hasMorePages.value = payload.data.value.entries.has_more_pages
    totalPages.value = payload.data.value.entries.last_page
  }
}

// Load Entries on filter change
const GqlInstance = useGql()
async function fetchEntries(desiredPage: number = page.value) {
  if (desiredPage !== undefined) {
    page.value = desiredPage
  }

  savePageInUrl()

  const payload = await GqlInstance('BlogArticles', {
    sort: 'date desc',
    limit: limit,
    page: page.value,
    filter: {
      site: locale.value,
      category: { contains: filter.value.category || null },
    },
  })

  if (!payload?.entries?.data) {
    console.error('Blog Articles could not be fetched')
    return
  }

  hasMorePages.value = payload.entries.has_more_pages
  totalPages.value = payload.entries.last_page

  entries.value = payload.entries.data as BlogArticleFragment[]
}

// Saves the filter in the URL
function saveFilterInUrl() {
  router.push({
    query: {
      ...route.query,
      category: filter.value.category,
    },
  })
}

function savePageInUrl() {
  router.push({
    query: {
      ...route.query,
      page: page.value,
    },
  })
}

// Watch for filter changes
watch(
  () => filter.value,
  () => {
    saveFilterInUrl()
    // Reset page to 1
    page.value = 1
    fetchEntries()
  },
  { deep: true },
)
</script>
